import { Injectable, Renderer2, RendererFactory2, RendererStyleFlags2, inject } from "@angular/core";
import { TinyColor, Numberify, RGBA } from "@ctrl/tinycolor";
import {
  HueValue,
  MatCssHueColorContrastMapItem,
  MatCssHueColorMapItem,
  MatCssPalettePrefix,
  MaterialCssVariables,
  MaterialCssVariablesConfig
} from "./material-css-vars.model";
import { DOCUMENT } from "@angular/common";
import { DEFAULT_MAT_CSS_CFG, MATERIAL_CSS_VARS_CFG } from "./material-css-vars.constants";
import { Subject } from "rxjs";

interface CssVariable {
  name: string;
  val: string;
}

@Injectable({
  providedIn: "root"
})
export class MaterialCssVarsService {
  private rendererFactory = inject(RendererFactory2);
  private document = inject<any>(DOCUMENT);
  darkModeChange: Subject<boolean> = new Subject<boolean>();

  private static CONTRAST_PREFIX = "contrast-";
  private static DARK_TEXT_VAR = "--dark-primary-text";
  private static LIGHT_TEXT_VAR = "--light-primary-text";
  private _renderer: Renderer2;
  private _ROOT: HTMLElement;

  private _cfg: MaterialCssVariablesConfig;
  private _primary: string;
  private _accent: string;
  private _warn: string;
  private _lightWorkspace: string;
  private _darkWorkspace: string;
  private _isDarkTheme: boolean;
  private _isAutoContrast: boolean = false;
  private _contrastColorThresholdPrimary: HueValue = "400";
  private _contrastColorThresholdAccent: HueValue = "400";
  private _contrastColorThresholdWarn: HueValue = "400";

  constructor() {
    const cfg = inject<MaterialCssVariablesConfig>(MATERIAL_CSS_VARS_CFG);

    this._renderer = this.rendererFactory.createRenderer(null, null);
    this._ROOT = this.document.documentElement;

    this._cfg = {
      ...DEFAULT_MAT_CSS_CFG,
      ...cfg
    };

    this._isAutoContrast = this.cfg.isAutoContrast;

    if (this.cfg.isDarkTheme) {
      this.setDarkTheme(this.cfg.isDarkTheme);
    }

    if (this.cfg.primary) {
      this.setPrimaryColor(this.cfg.primary);
    }

    if (this.cfg.accent) {
      this.setAccentColor(this.cfg.accent);
    }

    if (this.cfg.warn) {
      this.setWarnColor(this.cfg.warn);
    }

    if (this.cfg?.lightWorkspace) {
      this.setLightWorkspace(this.cfg.lightWorkspace);
    }

    if (this.cfg?.darkWorkspace) {
      this.setDarkWorkspace(this.cfg.darkWorkspace);
    }

    if (!this._isAutoContrast) {
      if (cfg.contrastPrimary) {
        this.setContrastColorThresholdPrimary(this._cfg.contrastPrimary);
      }
      if (cfg.contrastAccent) {
        this.setContrastColorThresholdAccent(this._cfg.contrastAccent);
      }
      if (cfg.contrastPrimary) {
        this.setContrastColorThresholdWarn(this._cfg.contrastWarn);
      }
    }
  }

  setPrimaryColor(hex: string) {
    if (!hex || hex.trim().length === 0) {
      return;
    }
    this._primary = hex;
    const varPrefix = MatCssPalettePrefix.Primary;
    const stylePrimary = this._computePaletteColors(varPrefix, this.primary);
    this._setStyle(stylePrimary);

    if (this.isAutoContrast) {
      this._recalculateAndSetContrastColor(varPrefix);
    }
  }

  setAccentColor(hex: string) {
    if (!hex || hex.trim().length === 0) {
      return;
    }
    this._accent = hex;
    const varPrefix = MatCssPalettePrefix.Accent;
    const styleAccent = this._computePaletteColors(varPrefix, this.accent);
    this._setStyle(styleAccent);
    if (this.isAutoContrast) {
      this._recalculateAndSetContrastColor(varPrefix);
    }
  }

  setWarnColor(hex: string) {
    if (!hex || hex.trim().length === 0) {
      return;
    }
    this._warn = hex;
    const varPrefix = MatCssPalettePrefix.Warn;
    const styleWarn = this._computePaletteColors(varPrefix, this.warn);
    this._setStyle(styleWarn);

    if (this.isAutoContrast) {
      this._recalculateAndSetContrastColor(varPrefix);
    }
  }

  setVariable(cssVarName: MaterialCssVariables, value: string) {
    if (!cssVarName || cssVarName.trim().length === 0) {
      return;
    }
    this._setStyle([
      {
        name: cssVarName,
        val: value
      }
    ]);
  }

  setLightWorkspace(hex: string) {
    if (!hex) {
      this.setVariable(MaterialCssVariables.LightWorkspace, this.defaultLightWorkspace);
    } else {
      this._lightWorkspace = hex;
      const c = new TinyColor(hex);
      const rgb = c.toRgb();
      this.setVariable(MaterialCssVariables.LightWorkspace, `rgb(${rgb.r},${rgb.g}, ${rgb.b})`);
    }
  }

  setDarkWorkspace(hex: string) {
    if (!hex) {
      this.setVariable(MaterialCssVariables.DarkWorkspace, this.defaultDarkWorkspace);
    } else {
      this._darkWorkspace = hex;
      const c = new TinyColor(hex);
      const rgb = c.toRgb();
      this.setVariable(MaterialCssVariables.DarkWorkspace, `rgb(${rgb.r},${rgb.g}, ${rgb.b})`);
    }
  }

  setDarkTheme(isDark: boolean) {
    this._isDarkTheme = isDark;
    if (isDark) {
      this.document.body.classList.remove(this.cfg.lightThemeClass);
      this.document.body.classList.add(this.cfg.darkThemeClass);
    } else {
      this.document.body.classList.remove(this.cfg.darkThemeClass);
      this.document.body.classList.add(this.cfg.lightThemeClass);
    }
    this.darkModeChange.next(isDark);
  }

  setAutoContrastEnabled(val: boolean) {
    this._isAutoContrast = val;
    if (val) {
      this._recalculateAndSetContrastColor(MatCssPalettePrefix.Primary);
      this._recalculateAndSetContrastColor(MatCssPalettePrefix.Accent);
      this._recalculateAndSetContrastColor(MatCssPalettePrefix.Warn);
    } else {
      this.setContrastColorThresholdPrimary(this.contrastColorThresholdPrimary);
      this.setContrastColorThresholdAccent(this.contrastColorThresholdAccent);
      this.setContrastColorThresholdWarn(this.contrastColorThresholdWarn);
    }
  }

  setContrastColorThresholdPrimary(threshold: HueValue) {
    if (!threshold || this._isAutoContrast) {
      return;
    }
    this._contrastColorThresholdPrimary = threshold;
    this._setContrastColorThreshold(threshold, MatCssPalettePrefix.Primary);
  }

  setContrastColorThresholdAccent(threshold: HueValue) {
    if (!threshold || this._isAutoContrast) {
      return;
    }
    this._contrastColorThresholdAccent = threshold;
    this._setContrastColorThreshold(threshold, MatCssPalettePrefix.Accent);
  }

  setContrastColorThresholdWarn(threshold: HueValue) {
    if (!threshold || this._isAutoContrast) {
      return;
    }
    this._contrastColorThresholdWarn = threshold;
    this._setContrastColorThreshold(threshold, MatCssPalettePrefix.Warn);
  }

  getPaletteForColor(hex: string): MatCssHueColorMapItem[] {
    if (!hex) {
      return [];
    }
    return this.cfg.colorMap.map((item) => {
      const c = this._computePalletTriad(hex, item.name);
      return {
        hue: item.name,
        isLight: c.isLight,
        color: {
          ...c.rgb,
          str: `rgb(${c.rgb.r},${c.rgb.g},${c.rgb.b})`
        }
      };
    });
  }

  getPaletteWithContrastForColor(hex: string): MatCssHueColorContrastMapItem[] {
    if (!hex) {
      return [];
    }
    const lightText = this._getCssVarValue(MaterialCssVarsService.LIGHT_TEXT_VAR);
    const darkText = this._getCssVarValue(MaterialCssVarsService.DARK_TEXT_VAR);
    const palette = this.getPaletteForColor(hex);
    return palette.map((item) => {
      const contrastStr = item.isLight ? lightText : darkText;

      const sLight = this._replaceNoRgbValue("", contrastStr)
        .split(",")
        .map((v) => +v);
      const cco = { r: sLight[0], g: sLight[1], b: sLight[2], a: 1 };
      return {
        ...item,
        contrast: {
          ...cco,
          str: `${cco.r},${cco.g},${cco.b}`
        }
      };
    });
  }

  rgbToHex(rgb: string) {
    return new TinyColor(`rgb(${rgb})`).toHex();
  }

  public get cfg(): MaterialCssVariablesConfig {
    return this._cfg;
  }

  public get primary(): string {
    return this._primary;
  }

  public get accent(): string {
    return this._accent;
  }

  public get warn(): string {
    return this._warn;
  }

  public get defaultLightWorkspace(): string {
    return this._getCssVarValue(MaterialCssVariables.LightWorkspaceDefault);
  }

  public get defaultDarkWorkspace(): string {
    return this._getCssVarValue(MaterialCssVariables.DarkWorkspaceDefault);
  }

  public get lightWorkspace(): string {
    return this._lightWorkspace;
  }

  public get darkWorkspace(): string {
    return this._darkWorkspace;
  }

  public get isDarkTheme(): boolean {
    return this._isDarkTheme;
  }

  public get isAutoContrast(): boolean {
    return this._isAutoContrast;
  }

  public get contrastColorThresholdPrimary(): HueValue {
    return this._contrastColorThresholdPrimary;
  }

  public get contrastColorThresholdAccent(): HueValue {
    return this._contrastColorThresholdAccent;
  }

  public get contrastColorThresholdWarn(): HueValue {
    return this._contrastColorThresholdWarn;
  }

  /**
   * Private functions
   */

  private _setContrastColorThreshold(threshold: HueValue, palettePrefix: MatCssPalettePrefix) {
    if (!threshold || !palettePrefix || this.isAutoContrast) {
      return;
    }
    let color = MaterialCssVarsService.DARK_TEXT_VAR;
    const updates = this.cfg.sortedHues.map((hue) => {
      if (hue === threshold) {
        color = MaterialCssVarsService.LIGHT_TEXT_VAR;
      }
      return {
        val: `var(${color})`,
        name: `${palettePrefix + MaterialCssVarsService.CONTRAST_PREFIX}${hue}`
      };
    });
    this._setStyle(updates);
  }

  private _computePaletteColors(prefix: MatCssPalettePrefix, hex: string): CssVariable[] {
    return this.getPaletteForColor(hex).map((item) => {
      const c = item.color;
      return {
        name: `${prefix}${item.hue}`,
        val: `rgb(${c.r}, ${c.g}, ${c.b})`
      };
    });
  }

  private _recalculateAndSetContrastColor(palettePrefix: MatCssPalettePrefix) {
    const updates = this._calculateContrastColorsForCurrentValues(palettePrefix).map(({ contrastColorVar, hue }) => {
      return {
        val: `var(${contrastColorVar})`,
        name: `${palettePrefix + MaterialCssVarsService.CONTRAST_PREFIX}${hue}`
      };
    });
    this._setStyle(updates);
  }

  private _calculateContrastColorsForCurrentValues(
    palettePrefix: MatCssPalettePrefix
  ): { contrastColorVar: string; hue: HueValue }[] {
    return this.cfg.sortedHues.map((hue) => {
      const hueVarVal = this._getCssVarValue(`${palettePrefix}${hue}`);
      const c = new TinyColor(`rgb(${hueVarVal})`);
      const contrastColorVar = c.isDark()
        ? MaterialCssVarsService.LIGHT_TEXT_VAR
        : MaterialCssVarsService.DARK_TEXT_VAR;
      return {
        contrastColorVar,
        hue
      };
    });
  }

  private _setStyle(vars: CssVariable[]) {
    vars.forEach((s) => {
      this._renderer.setStyle(this._ROOT, s.name, s.val, RendererStyleFlags2.DashCase);
      this._renderer.setStyle(
        this._ROOT,
        s.name + "-no-rgb",
        this._replaceNoRgbValue(s.name, s.val),
        RendererStyleFlags2.DashCase
      );
    });
  }

  /**
   * Replace variables that are formatted as rgba(var(rgb(xxx))) to be var(xxx) to allow proper formatting
   * in variable overrides.
   * @param value
   * @returns
   */
  private _replaceNoRgbValue(name: string, value: string) {
    const isContrast: boolean = name.includes(MaterialCssVarsService.CONTRAST_PREFIX);
    let noRgb: string = "";
    if (isContrast) {
      noRgb = value.replace(")", "-no-rgb)");
    } else {
      noRgb = value.replace("rgba(", "").replace("rgb(", "").replace(")", "");
      if (noRgb.startsWith("var(")) {
        noRgb = noRgb.concat(")");
      }
    }
    return noRgb;
  }

  private _getCssVarValue(v: string): string {
    return getComputedStyle(this._ROOT).getPropertyValue(v);
  }

  /**
   * Compute pallet colors based on a Triad (Constantin)
   * see: https://github.com/mbitson/mcg
   * @param hex
   * @param hue
   */
  private _computePalletTriad(hex: string, hue: HueValue) {
    const baseLight = new TinyColor("#ffffff");
    const baseDark = this._multiply(new TinyColor(hex).toRgb(), new TinyColor(hex).toRgb());
    const baseTriad = new TinyColor(hex).tetrad();
    let color: { rgb: Numberify<RGBA>; isLight: boolean };

    switch (hue) {
      case "50":
        color = this._getColorObject(baseLight.mix(hex, 12));
        break;
      case "100":
        color = this._getColorObject(baseLight.mix(hex, 30));
        break;
      case "200":
        color = this._getColorObject(baseLight.mix(hex, 50));
        break;
      case "300":
        color = this._getColorObject(baseLight.mix(hex, 70));
        break;
      case "400":
        color = this._getColorObject(baseLight.mix(hex, 85));
        break;
      case "500":
        color = this._getColorObject(baseLight.mix(hex, 100));
        break;
      case "600":
        color = this._getColorObject(baseDark.mix(hex, 87));
        break;
      case "700":
        color = this._getColorObject(baseDark.mix(hex, 70));
        break;
      case "800":
        color = this._getColorObject(baseDark.mix(hex, 54));
        break;
      case "900":
        color = this._getColorObject(baseDark.mix(hex, 25));
        break;
      case "A100":
        color = this._getColorObject(baseDark.mix(baseTriad[4], 15).saturate(80).lighten(65));
        break;
      case "A200":
        color = this._getColorObject(baseDark.mix(baseTriad[4], 15).saturate(80).lighten(55));
        break;
      case "A400":
        color = this._getColorObject(baseDark.mix(baseTriad[4], 15).saturate(100).lighten(45));
        break;
      case "A700":
        color = this._getColorObject(baseDark.mix(baseTriad[4], 15).saturate(100).lighten(40));
        break;
      default:
        break;
    }
    return color;
  }

  private _multiply(rgb1: Numberify<RGBA>, rgb2: Numberify<RGBA>): TinyColor {
    rgb1.b = Math.floor((rgb1.b * rgb2.b) / 255);
    rgb1.g = Math.floor((rgb1.g * rgb2.g) / 255);
    rgb1.r = Math.floor((rgb1.r * rgb2.r) / 255);
    return new TinyColor("rgb " + rgb1.r + " " + rgb1.g + " " + rgb1.b);
  }

  private _getColorObject(value: TinyColor) {
    const c = new TinyColor(value);
    return { rgb: c.toRgb(), isLight: c.isLight() };
  }
}
